import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import _ from 'lodash';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import {
  compareBy_id,
  compareBy_idAndErrors,
  createListComparator,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  AppPersonalizedOfferGroup,
  SanityOfferListItem,
  isAppPersonalizedOffer,
  isSanityOfferListItem,
} from 'state/loyalty/types';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeLive } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import {
  TransformTierGroupsToOffersOptions_Legacy,
  transformTierGroupsToOffers_Legacy,
} from './tiered-offers/legacy';
import {
  TransformTierGroupsToOffersOptions_UnifiedMenu,
  transformTierGroupsToOffers_UnifiedMenu,
} from './tiered-offers/one-menu';

const compareByPogIdAndUpdatedAt = createListComparator(['id', 'updatedAt']);
const didSanityOfferListChange = (previous: SanityOfferListItem[], next: SanityOfferListItem[]) =>
  compareBy_id(previous, next) || !_.isEqual(previous, next);

const didPersonalizedOfferGroupsChange = (
  previous: AppPersonalizedOfferGroup[],
  next: AppPersonalizedOfferGroup[]
) => compareByPogIdAndUpdatedAt(previous, next) || !_.isEqual(previous, next);

export function useGetCmsAndLoyaltyOffers() {
  const [refreshOffersLoading, setRefreshOffersLoading] = useState(false);
  const {
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    loyaltyStandardOffersEnabled,
    ensureSortedOffersLoaded,
    offersError,
  } = useLoyaltyContext();
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const dispatch = useAppDispatch();
  const { activeDayParts } = useMainMenuContext();
  const dayPartsRef = useRef({
    setActiveDayPartOnLoad: false,
    prevDayParts: [] as IDayPartBoundary[],
  });

  const sanityOfferListItemsWithExtras = useAppSelector(selectors.loyalty.selectCmsOffers);
  // Personalized offers are provided to reduceOffers authoritatively via
  // `appPersonalizedOffers`. They must not also flow through the CMS-backed sanity
  // list: `setCmsOffers` merges offers cumulatively (uniqBy `_id`) and never evicts
  // a redeemed personalized offer, so including them here would re-surface offers
  // the engine has already dropped after redemption.
  const [sanityOfferListItems, setSanityOfferListItems] = useConditionalState(
    () =>
      sanityOfferListItemsWithExtras.filter(
        item => isSanityOfferListItem(item) && !isAppPersonalizedOffer(item)
      ),
    didSanityOfferListChange
  );
  const serviceMode = useServiceModeLive();
  const { store, prices } = useStoreContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();

  const userEngineOfferListItems = useAppSelector(selectors.loyalty.selectEngineOfferListItems);

  const _appPersonalizedOffers = useAppSelector(selectors.loyalty.selectAppPersonalizedOffers);
  const [appPersonalizedOffers, setAppPersonalizedOffers] = useConditionalState(
    _appPersonalizedOffers,
    compareBy_idAndErrors
  );

  const _appPersonalizedOfferGroups = useAppSelector(
    selectors.loyalty.selectAppPersonalizedOfferGroups
  );
  const [appPersonalizedOfferGroups, setAppPersonalizedOfferGroups] = useConditionalState(
    _appPersonalizedOfferGroups as AppPersonalizedOfferGroup[],
    didPersonalizedOfferGroupsChange
  );
  const guestEngineOfferListItems = useAppSelector(
    selectors.loyalty.selectGuestEngineOfferListItems
  );

  const transformedSanityOffers = useMemo(() => {
    if (isUnifiedMenuEnabled && !isUnifiedMenuLoading && indexedMenu && serviceMode) {
      return transformTierGroupsToOffers_UnifiedMenu({
        items: sanityOfferListItemsWithExtras as TransformTierGroupsToOffersOptions_UnifiedMenu['items'],
        indexedMenu,
        serviceMode,
      });
    } else if (!isUnifiedMenuEnabled && store._id && prices && serviceMode) {
      return transformTierGroupsToOffers_Legacy({
        items: sanityOfferListItemsWithExtras as TransformTierGroupsToOffersOptions_Legacy['items'],
        store,
        serviceMode,
        prices,
      });
    }
    return [];
  }, [
    sanityOfferListItemsWithExtras,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    indexedMenu,
    serviceMode,
    store,
    prices,
  ]);

  useEffect(() => {
    setSanityOfferListItems(
      // @ts-expect-error TS2345 - transformedSanityOffers is SanityOfferListItem[]
      transformedSanityOffers.filter(item => !isAppPersonalizedOffer(item))
    );
  }, [transformedSanityOffers, setSanityOfferListItems]);

  useEffect(() => {
    setAppPersonalizedOffers(_appPersonalizedOffers);
  }, [_appPersonalizedOffers, setAppPersonalizedOffers]); // setPersonalizedOffers is stable but ESLint doesn't know that

  useEffect(() => {
    setAppPersonalizedOfferGroups(_appPersonalizedOfferGroups as AppPersonalizedOfferGroup[]);
  }, [_appPersonalizedOfferGroups, setAppPersonalizedOfferGroups]);

  const engineOfferListItems =
    !loyaltyUserIdLoading && loyaltyUserId ? userEngineOfferListItems : guestEngineOfferListItems;

  const previousLoyaltyUserIdRef = useRef(loyaltyUserId);

  useEffect(() => {
    if (previousLoyaltyUserIdRef.current === loyaltyUserId) {
      return;
    }

    previousLoyaltyUserIdRef.current = loyaltyUserId;
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
  }, [dispatch, loyaltyUserId]);

  // This refers to userOffers, personalizedOffers, cmsOffers and unauthenticatedOffers
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  // Lazy-load sortedSystemwideOffers when Offers screen mounts; avoids fetching at app start
  useEffect(() => {
    if (loyaltyStandardOffersEnabled) {
      ensureSortedOffersLoaded();
    }
  }, [loyaltyStandardOffersEnabled, ensureSortedOffersLoaded]);

  const fetchOffers = useCallback(async () => {
    // Reset shouldRefetchOffers flag
    dispatch(actions.loyalty.setShouldRefetchOffers(false));

    if (loyaltyStandardOffersEnabled) {
      if (loyaltyUserId) {
        await queryLoyaltyUserStandardOffers({ loyaltyId: loyaltyUserId });
      } else {
        await queryLoyaltyOffers();
      }
    }
  }, [
    loyaltyUserId,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    dispatch,
  ]);

  // NOTE: this is used for initial offers load as well
  // TODO make it only fire for refresh (not initial load) but tricky refactor is required here
  const refreshOffers = useCallback(async () => {
    setRefreshOffersLoading(true);

    try {
      // The CMS response is generally static and expensive, so do not refetch it during a normal
      // refresh. If its initial request failed, ensureSortedOffersLoaded retries because it has not
      // marked that dependency as fetched yet.
      if (offersError) {
        await ensureSortedOffersLoaded();
      }
      await fetchOffers();
    } finally {
      setRefreshOffersLoading(false);
    }
  }, [ensureSortedOffersLoaded, fetchOffers, offersError]);

  useEffect(() => {
    if (activeDayParts.length === 0) {
      return;
    }

    if (!_.isEqual(activeDayParts, dayPartsRef.current.prevDayParts)) {
      if (!dayPartsRef.current.setActiveDayPartOnLoad) {
        dayPartsRef.current.setActiveDayPartOnLoad = true;
      } else {
        refreshOffers();
      }

      dayPartsRef.current.prevDayParts = activeDayParts;
    }
  }, [activeDayParts, refreshOffers]);

  return {
    sanityOfferListItems: sanityOfferListItems as SanityOfferListItem[],
    appPersonalizedOffers,
    appPersonalizedOfferGroups,
    engineOfferListItems,
    offersLoading,
    offersError,
    refreshOffers,
    refreshOffersLoading,
    storeId: store?.number ?? undefined,
    serviceMode: serviceMode ?? undefined,
  };
}
